
/**
 * Masks email addresses for display
 * Example: "john.doe@example.com" -> "john******@******.com"
 * @param {string} email - The email address to mask
 * @returns {string} - Masked email address or original if masking is disabled
 */
export const maskEmail = email => {
  if (!email || typeof email !== 'string') return '';
  
  // If privacy masking is disabled, return original
  if (!isPrivacyMaskingEnabled()) {
    return email;
  }

  const [localPart, domain] = email.split('@');
  if (!localPart || !domain) return email;

  // Mask local part: show first 3 chars, mask the rest
  const localMasked =
    localPart.length > 3
      ? `${localPart.substring(0, 3)}${'*'.repeat(Math.min(localPart.length - 3, 8))}`
      : `${localPart.substring(0, 1)}${'*'.repeat(Math.min(localPart.length - 1, 4))}`;

  // Mask domain: mask everything except last 3 chars before TLD
  const domainParts = domain.split('.');
  const tld = domainParts.pop() || '';
  const domainName = domainParts.join('.');

  let domainMasked = '';
  if (domainName.length > 0) {
    domainMasked = `${'*'.repeat(Math.min(domainName.length, 6))}.${tld}`;
  } else {
    domainMasked = `******.${tld}`;
  }

  return `${localMasked}@${domainMasked}`;
};

/**
 * Checks if privacy masking is enabled from environment/config
 * @returns {boolean} - true if masking is enabled (1), false if disabled (0)
 */
const isPrivacyMaskingEnabled = () => {
  // Check window config first (set from backend)
  if (window.chatwootConfig?.privacyMaskingEnabled !== undefined) {
    return window.chatwootConfig.privacyMaskingEnabled === 1 || window.chatwootConfig.privacyMaskingEnabled === '1';
  }
  // Default to enabled (1) if not specified
  return true;
};

/**
 * Masks phone numbers for display
 * Example: "+905321234567" -> "+9053*******567"
 * @param {string} phoneNumber - The phone number to mask
 * @returns {string} - Masked phone number or original if masking is disabled
 */
export const maskPhoneNumber = phoneNumber => {
  if (!phoneNumber || typeof phoneNumber !== 'string') return '';
  
  // If privacy masking is disabled, return original
  if (!isPrivacyMaskingEnabled()) {
    return phoneNumber;
  }

  // Remove all non-digit characters except +
  const cleaned = phoneNumber.replace(/[^\d+]/g, '');

  if (cleaned.length <= 6) {
    // Too short, mask all but first 2 chars
    return `${cleaned.substring(0, 2)}${'*'.repeat(cleaned.length - 2)}`;
  }

  // Show first 4-5 digits (country code + area code), mask middle, show last 3
  const showStart = Math.min(4, Math.floor(cleaned.length / 3));
  const showEnd = 3;
  const start = cleaned.substring(0, showStart);
  const end = cleaned.substring(cleaned.length - showEnd);
  const middle = '*'.repeat(cleaned.length - showStart - showEnd);

  return `${start}${middle}${end}`;
};

/**
 * Encodes a string to base64
 * @param {string} str - String to encode
 * @returns {string} - Base64 encoded string
 */
export const encodeBase64 = str => {
  if (typeof window !== 'undefined' && window.btoa) {
    return window.btoa(encodeURIComponent(str));
  }
  // Fallback for Node.js environment
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(str, 'utf-8').toString('base64');
  }
  return str;
};

/**
 * Decodes a base64 string
 * @param {string} str - Base64 string to decode
 * @returns {string} - Decoded string
 */
export const decodeBase64 = str => {
  if (typeof window !== 'undefined' && window.atob) {
    return decodeURIComponent(window.atob(str));
  }
  // Fallback for Node.js environment
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(str, 'base64').toString('utf-8');
  }
  return str;
};

/**
 * Creates an encrypted contact link data attribute
 * @param {string} type - 'email' or 'phone'
 * @param {string} value - The actual email or phone number
 * @param {Object} options - Additional options including custom_attributes and additional_attributes
 * @returns {string} - Base64 encoded data string
 */
export const createEncryptedContactLink = (type, value, options = {}) => {
  if (!value || !type) return '';
  const data = {
    type,
    value,
    custom_attributes: options.custom_attributes || {},
    additional_attributes: options.additional_attributes || {},
  };
  return encodeBase64(JSON.stringify(data));
};

/**
 * Decrypts contact link data attribute
 * @param {string} encryptedData - Base64 encoded data
 * @returns {Object|null} - { type: 'email'|'phone', value: string, custom_attributes: {}, additional_attributes: {} } or null
 */
export const decryptContactLink = encryptedData => {
  if (!encryptedData) return null;
  try {
    const decoded = decodeBase64(encryptedData);
    const parsed = JSON.parse(decoded);
    // Ensure backward compatibility
    return {
      type: parsed.type,
      value: parsed.value,
      custom_attributes: parsed.custom_attributes || {},
      additional_attributes: parsed.additional_attributes || {},
    };
  } catch (error) {
    console.error('Failed to decrypt contact link:', error);
    return null;
  }
};

/**
 * Handles encrypted contact link click
 * Sends POST request to backend proxy endpoint which forwards to external CRM
 * @param {string} encryptedData - Base64 encoded contact data
 */
export const handleEncryptedContactClick = async encryptedData => {
  const contactData = decryptContactLink(encryptedData);
  if (!contactData) {
    console.error('Invalid contact link data');
    return;
  }

  try {
    // Get account ID from URL
    const pathParts = window.location.pathname.split('/');
    const accountIndex = pathParts.indexOf('accounts');
    const accountId = accountIndex !== -1 ? pathParts[accountIndex + 1] : null;

    if (!accountId) {
      console.error('Could not determine account ID from URL');
      return;
    }

    // Get API base URL from config
    const { apiHost = '' } = window.chatwootConfig || {};
    const apiUrl = `${apiHost}/api/v1/accounts/${accountId}/contacts/request_contact_info`;

    // Get auth headers from cookies
    const headers = {
      'Content-Type': 'application/json',
    };

    // Try to get auth data from cookies (same way Auth helper does)
    try {
      const Cookies = (await import('js-cookie')).default;
      const sessionInfo = Cookies.get('cw_d_session_info');
      if (sessionInfo) {
        const authData = JSON.parse(sessionInfo);
        if (authData) {
          headers['access-token'] = authData['access-token'];
          headers['token-type'] = authData['token-type'];
          headers['client'] = authData['client'];
          headers['expiry'] = authData['expiry'];
          headers['uid'] = authData['uid'];
        }
      }
    } catch (e) {
      // If cookie parsing fails, continue without auth headers
      console.warn('Could not parse auth cookies:', e);
    }

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers,
      credentials: 'same-origin',
      body: JSON.stringify({
        type: contactData.type,
        value: contactData.value,
        custom_attributes: contactData.custom_attributes || {},
        additional_attributes: contactData.additional_attributes || {},
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Failed to send contact request:', errorData.error || response.statusText);
      return;
    }

    const responseData = await response.json().catch(() => ({}));
    
    // If response indicates we should open in new window (HTML response from CRM)
    if (responseData.extension && responseData.url) {
      // Open CRM form in new window with POST method
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = responseData.url;
      form.target = '_blank';
      form.style.display = 'none';
      
      // Add payload as form fields
      if (responseData.payload) {
        Object.keys(responseData.payload).forEach(key => {
          const value = responseData.payload[key];
          if (value !== null && value !== undefined) {
            if (typeof value === 'object') {
              // For nested objects, stringify them
              const input = document.createElement('input');
              input.type = 'hidden';
              input.name = key;
              input.value = JSON.stringify(value);
              form.appendChild(input);
            } else {
              const input = document.createElement('input');
              input.type = 'hidden';
              input.name = key;
              input.value = value;
              form.appendChild(input);
            }
          }
        });
      }
      
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
      
      return;
    }
    
    // If response indicates modal should be opened
    if (responseData.modal && responseData.data) {
      // Emit event to open modal with response data
      const event = new CustomEvent('openContactInfoModal', {
        detail: {
          type: contactData.type,
          value: contactData.value,
          responseData: responseData.data,
        },
      });
      window.dispatchEvent(event);
    }
    
    // If success is false, show error
    if (responseData.success === false) {
      console.error('Contact request failed:', responseData.error);
      // You can emit an error event here if needed
      const errorEvent = new CustomEvent('contactRequestError', {
        detail: {
          error: responseData.error,
          type: contactData.type,
        },
      });
      window.dispatchEvent(errorEvent);
    }
  } catch (error) {
    console.error('Error sending contact request:', error);
  }
};

